import React, { useState, useEffect } from "react";
import { 
  Shield, AlertTriangle, Lock, Unlock, RefreshCw, 
  Clock, User, Globe, Activity, Ban, CheckCircle,
  XCircle, Eye, Filter, Search, ChevronDown
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { format } from "date-fns";

const AdminSecurity = () => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [rateLimitStatus, setRateLimitStatus] = useState(null);
  const [stats, setStats] = useState({
    totalEvents: 0,
    typeBreakdown: [],
    topIps: []
  });
  const [filters, setFilters] = useState({
    eventType: "",
    hours: 24
  });
  const [unblockingIp, setUnblockingIp] = useState(null);

  const fetchSecurityData = async () => {
    setLoading(true);
    try {
      // Fetch security events
      const eventsRes = await api.get("/admin/security/events", {
        params: {
          event_type: filters.eventType || undefined,
          hours: filters.hours,
          limit: 100
        }
      });
      
      setEvents(eventsRes.data.events || []);
      setStats({
        totalEvents: eventsRes.data.total_events || 0,
        typeBreakdown: eventsRes.data.type_breakdown || [],
        topIps: eventsRes.data.top_ips || []
      });

      // Fetch rate limit status
      const rateLimitRes = await api.get("/admin/security/rate-limits");
      setRateLimitStatus(rateLimitRes.data);
      
    } catch (error) {
      console.error("Error fetching security data:", error);
      // Don't show error toast if it's just no data
      if (error.response?.status !== 404) {
        toast.error("Failed to load security data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSecurityData();
  }, [filters]);

  const handleUnblockIp = async (ip) => {
    setUnblockingIp(ip);
    try {
      await api.post("/admin/security/unblock-ip", { ip });
      toast.success(`IP ${ip} unblocked`);
      fetchSecurityData();
    } catch (error) {
      toast.error("Failed to unblock IP");
    } finally {
      setUnblockingIp(null);
    }
  };

  const getEventIcon = (type) => {
    switch (type) {
      case "failed_login":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "rate_limit":
        return <Ban className="w-4 h-4 text-orange-500" />;
      case "suspicious_request":
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getEventBadgeColor = (type) => {
    switch (type) {
      case "failed_login":
        return "bg-red-100 text-red-700";
      case "rate_limit":
        return "bg-orange-100 text-orange-700";
      case "suspicious_request":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-security-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-[#1C3A7A]" />
              Security Dashboard
            </h1>
            <p className="text-gray-500 mt-1">Monitor security events and manage blocked IPs</p>
          </div>
          <button
            onClick={fetchSecurityData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-[#1C3A7A] text-white rounded-lg hover:bg-[#15306a] transition disabled:opacity-50"
            data-testid="refresh-security-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-4" data-testid="total-events-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Events</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalEvents}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Activity className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Last {filters.hours} hours</p>
          </div>

          <div className="bg-white rounded-xl border p-4" data-testid="blocked-ips-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Blocked IPs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {rateLimitStatus?.blocked_ips_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <Ban className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Currently blocked</p>
          </div>

          <div className="bg-white rounded-xl border p-4" data-testid="failed-logins-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Failed Logins</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.typeBreakdown.find(t => t.type === "failed_login")?.count || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <XCircle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Last {filters.hours} hours</p>
          </div>

          <div className="bg-white rounded-xl border p-4" data-testid="rate-limits-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rate Limited</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.typeBreakdown.find(t => t.type === "rate_limit")?.count || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Last {filters.hours} hours</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Security Events Table */}
          <div className="lg:col-span-2 bg-white rounded-xl border" data-testid="security-events-section">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Security Events
              </h2>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mt-3">
                <select
                  value={filters.eventType}
                  onChange={(e) => setFilters({ ...filters, eventType: e.target.value })}
                  className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent"
                  data-testid="event-type-filter"
                >
                  <option value="">All Event Types</option>
                  <option value="failed_login">Failed Logins</option>
                  <option value="rate_limit">Rate Limited</option>
                  <option value="suspicious_request">Suspicious Requests</option>
                </select>

                <select
                  value={filters.hours}
                  onChange={(e) => setFilters({ ...filters, hours: parseInt(e.target.value) })}
                  className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent"
                  data-testid="time-range-filter"
                >
                  <option value={1}>Last 1 hour</option>
                  <option value={6}>Last 6 hours</option>
                  <option value={24}>Last 24 hours</option>
                  <option value={48}>Last 48 hours</option>
                  <option value={168}>Last 7 days</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                  <p className="text-gray-500 mt-2">Loading events...</p>
                </div>
              ) : events.length === 0 ? (
                <div className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
                  <p className="text-gray-900 font-medium mt-2">All Clear!</p>
                  <p className="text-gray-500 text-sm">No security events in this time period</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Path</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {events.map((event, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${getEventBadgeColor(event.type)}`}>
                            {getEventIcon(event.type)}
                            {event.type?.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-600">
                          {event.ip || "Unknown"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">
                          {event.path || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {event.timestamp ? format(new Date(event.timestamp), "MMM d, HH:mm:ss") : "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-[150px] truncate">
                          {event.details?.email || event.user_agent?.substring(0, 30) || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Blocked IPs */}
            <div className="bg-white rounded-xl border" data-testid="blocked-ips-section">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Ban className="w-5 h-5 text-red-500" />
                  Blocked IPs
                </h2>
              </div>
              <div className="p-4">
                {rateLimitStatus?.blocked_ips?.length > 0 ? (
                  <ul className="space-y-2">
                    {rateLimitStatus.blocked_ips.map((ip, index) => (
                      <li key={index} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                        <span className="font-mono text-sm text-red-700">{ip}</span>
                        <button
                          onClick={() => handleUnblockIp(ip)}
                          disabled={unblockingIp === ip}
                          className="p-1.5 bg-white text-red-600 rounded-lg hover:bg-red-100 transition disabled:opacity-50"
                          title="Unblock IP"
                          data-testid={`unblock-ip-${ip}`}
                        >
                          {unblockingIp === ip ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Unlock className="w-4 h-4" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-4">
                    <CheckCircle className="w-8 h-8 mx-auto text-green-500" />
                    <p className="text-sm text-gray-500 mt-2">No blocked IPs</p>
                  </div>
                )}
              </div>
            </div>

            {/* Top IPs */}
            <div className="bg-white rounded-xl border" data-testid="top-ips-section">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-500" />
                  Top IPs by Events
                </h2>
              </div>
              <div className="p-4">
                {stats.topIps.length > 0 ? (
                  <ul className="space-y-2">
                    {stats.topIps.map((item, index) => (
                      <li key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="font-mono text-sm text-gray-700">{item.ip || "Unknown"}</span>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          {item.count} events
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No data</p>
                )}
              </div>
            </div>

            {/* Rate Limit Config */}
            <div className="bg-white rounded-xl border" data-testid="rate-limit-config-section">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#1C3A7A]" />
                  Rate Limit Config
                </h2>
              </div>
              <div className="p-4">
                {rateLimitStatus?.configurations ? (
                  <ul className="space-y-2 text-sm">
                    {Object.entries(rateLimitStatus.configurations).slice(0, 5).map(([key, config]) => (
                      <li key={key} className="flex items-center justify-between">
                        <span className="text-gray-600">{key.replace(/_/g, " ")}</span>
                        <span className="font-mono text-gray-900">{config.max}/{config.window}s</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">Loading...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSecurity;
